import { motion } from 'framer-motion';
import { Button } from '../../../ui';
import {
  HeartIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';

interface WelcomeStepProps {
  onContinue: () => void;
}

const features = [
  {
    icon: CalendarDaysIcon,
    title: 'Track Your Journey',
    description: 'Daily check-ins and sobriety tracking to celebrate every milestone',
  },
  {
    icon: BookOpenIcon,
    title: 'Big Book Wisdom',
    description: 'Daily reflections and guidance rooted in time-tested principles',
  },
  {
    icon: UserGroupIcon,
    title: 'Sponsor Connection',
    description: 'Stay connected with your support network securely',
  },
  {
    icon: HeartIcon,
    title: 'Nightly Review',
    description: 'Reflect, grow, and build a strong foundation for tomorrow',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  },
};

export function WelcomeStep({ onContinue }: WelcomeStepProps) {
  return (
    <div className="text-center">
      {/* Inspirational quote */}
      <motion.div
        className="mb-10"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <blockquote className="relative">
          <div
            className="absolute -top-4 left-1/2 -translate-x-1/2 text-6xl text-primary-200 dark:text-primary-800 font-serif select-none"
            aria-hidden="true"
          >
            "
          </div>
          <p className="font-serif text-xl sm:text-2xl text-surface-700 dark:text-surface-200 italic leading-relaxed pt-4">
            We are going to know a new freedom and a new happiness.
          </p>
          <footer className="mt-3 text-sm text-surface-500 dark:text-surface-400">
            — The Big Book, Page 83
          </footer>
        </blockquote>
      </motion.div>

      {/* Feature cards */}
      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {features.map((feature) => (
          <motion.div
            key={feature.title}
            variants={itemVariants}
            className="group relative bg-white dark:bg-surface-800 rounded-2xl p-5 text-left shadow-sm border border-surface-100 dark:border-surface-700 hover:shadow-md hover:border-primary-200 dark:hover:border-primary-700 transition-all duration-300"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-2.5 rounded-xl bg-gradient-to-br from-primary-100 to-accent-50 dark:from-primary-900/50 dark:to-accent-900/30 group-hover:from-primary-200 group-hover:to-accent-100 dark:group-hover:from-primary-800/50 dark:group-hover:to-accent-800/30 transition-colors">
                <feature.icon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="font-semibold text-surface-800 dark:text-white text-sm">
                  {feature.title}
                </h3>
                <p className="mt-1 text-xs text-surface-500 dark:text-surface-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Privacy note */}
      <motion.p
        className="text-sm text-surface-500 dark:text-surface-400 mb-4 max-w-md mx-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        Your journey is private. We use end-to-end encryption and never share your personal
        recovery data.
      </motion.p>

      {/* Time expectation */}
      <motion.p
        className="text-xs text-surface-400 dark:text-surface-500 mb-8 max-w-md mx-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.65 }}
      >
        Setup takes just 2-3 minutes
      </motion.p>

      {/* CTA Button */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <Button onClick={onContinue} size="lg" className="min-w-[200px]">
          Begin Setup
        </Button>
      </motion.div>
    </div>
  );
}
